import AxiosInstance from 'src/shared/services/axios/interceptor';
import { AssociationStats } from 'src/shared/models/association/associationStats';
import { Association } from 'src/shared/models/association/association';
import { PagedResult } from 'src/shared/models/pagedResult';
import { AssociationUserResponse } from 'src/modules/associations/models/associationUserResponse';
import { AssocationTitleResponse } from 'src/modules/associations/models/assocationTitleResponse';
import { AssociationTitleRequest } from 'src/modules/associations/models/associationTitleRequest';
import { AssocationTitleConfigRequest } from 'src/modules/associations/models/associationTitleConfigRequest';
import { AssocationTitleConfigResponse } from 'src/modules/associations/models/assTitle';
import { AssociationRequest } from 'src/modules/associations/models/associationRequest';
import { AssociationResponse } from 'src/modules/associations/models/associationResponse';
import { AssociationFeeRequest } from 'src/modules/associations/models/associationFeeRequest';
import { AssociationFee } from 'src/modules/associations/models/associationFee';
import { AssociationSearchFilter } from 'src/modules/associations/models/associationSearchFilter';
import { setPagingParams } from '../functions/createPagingParams';

const controller = 'Association';

export const getAssociationStats = (id) => {
  const endpoint = `${controller}/${id}/stats`;
  return AxiosInstance.get<AssociationStats>(endpoint);
};

export const getAssociations = (districtId?, searchFilter?: AssociationSearchFilter) => {
  let endpoint = `${controller}?pageSize=10000`;

  if (districtId && !searchFilter) {
    endpoint += `&districtId=${districtId}`;
  } else if (searchFilter && !districtId) {
    endpoint = `${controller}/?${setPagingParams({
      pageIndex: searchFilter.pageIndex ?? 0,
      pageSize: searchFilter.pageSize ?? 25,
      sortCol: searchFilter.sortCol ?? '',
      sortDir: searchFilter.sortDir ?? 'asc',
    })}`;

    endpoint += searchFilter.filter ? `&filter=${searchFilter.filter}` : '';
    if (searchFilter.status !== undefined && searchFilter.status !== null) {
      endpoint += `&status=${searchFilter.status}`;
    }
  }

  return AxiosInstance.get<PagedResult<Association>>(endpoint);
};

export const getAssociationById = (id: string | undefined) => {
  const endpoint = `${controller}/${id}`;
  return AxiosInstance.get<AssociationResponse>(endpoint);
};

export const getAssociationFeeById = (id: string | undefined) => {
  const endpoint = `${controller}/${id}/fee`;
  return AxiosInstance.get<AssociationFee>(endpoint);
};

export const updateAssociation = (data: AssociationRequest) => {
  return AxiosInstance.put(controller, data);
};

export const getAssociationUsers = (id: string | undefined) => {
  return AxiosInstance.get<AssociationUserResponse[]>(`${controller}/${id}/User`);
};

export const getAssociationTitleByType = (id: string | undefined, type: number, includeResigned = false) => {
  return AxiosInstance.get<AssocationTitleResponse[]>(`${controller}/${id}/Title/${type}?includeResigned=${includeResigned}`);
};

export const updateAssociationTitle = (id: string | undefined, data: AssociationTitleRequest) => {
  return AxiosInstance.put(`${controller}/${id}/Title`, data);
};

export const getAssociationTitleConfigByType = (id: string | undefined, type: number) => {
  return AxiosInstance.get<AssocationTitleConfigResponse[]>(`${controller}/${id}/TitleConfig/${type}`);
};

// Skapa användarkonto åt en styrelsemedlem + skicka invite-mail.
// Prefill för invite-modalen: medlemsuppgifter + default välkomstmail + om access redan finns.
export type UserInvitePreview = {
  subject: string;
  body: string;
  firstName: string;
  lastName: string;
  email: string;
  personalIdentityNumber: string;
  hasUserAccess: boolean;
};

export type UserInviteRequest = {
  memberId: number;
  firstName: string;
  lastName: string;
  email: string;
  personalIdentityNumber: string;
  emailLoginEnabled: boolean;
  bankIdLoginEnabled: boolean;
  subject: string;
  body: string;
};

// Hämtar prefill + default välkomstmail att visa/redigera i invite-modalen.
// skipErrorToast: bakgrundsuppslag – modalen hanterar utebliven data själv.
export const getUserInviteEmail = (id: string | number | undefined, memberId: number) => {
  return AxiosInstance.get<UserInvitePreview>(`${controller}/${id}/UserInviteEmail?memberId=${memberId}`, { skipErrorToast: true });
};

// Skapar (eller länkar) användarkonto åt medlemmen och köar invite-mailet.
// skipErrorToast: felet hanteras med egen toast i modalen (undviker dubbla toaster).
export const inviteUserFromMember = (id: string | number | undefined, data: UserInviteRequest) => {
  return AxiosInstance.post(`${controller}/${id}/UserInvite`, data, { skipErrorToast: true });
};

export const updateAssociationTitleConfig = (id: string | undefined, data: AssocationTitleConfigRequest) => {
  return AxiosInstance.put(`${controller}/${id}/TitleConfig`, data);
};

export const updateAssociationFee = (data: AssociationFeeRequest) => {
  return AxiosInstance.put(`${controller}/${data.localAss_id}/fee`, data);
};

export const uploadAssociationLogo = (id: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return AxiosInstance.post<{ imageId: number }>(`${controller}/${id}/Logo`, formData);
};

export const deleteAssociationLogo = (id: number) => {
  return AxiosInstance.delete(`${controller}/${id}/Logo`);
};

export const getAssociationLogoUrl = (id: number) => {
  return `${controller}/${id}/Logo`;
};
